// Duplicate-fee detection — a DATA-ENTRY GUARDRAIL, not a money calculation.
//
// WHAT IT DETECTS
// The schema lets the same economic fee be entered twice on one order: once as a
// service-type LINE ITEM (`serviceType` = "rush", "setup_fee", or "other_service",
// with its own unit_price/net_cost) and again as a DECORATION CHARGE
// (`chargeType` = "rush", "setup", or "other" on a line_item_decoration_charges
// row). Both feed the document totals, and — since the all-inclusive
// commission-margin change — both feed booked cost/margin. So a rep who adds a
// "$25 rush" service line AND a "$25 rush" charge on a decoration (or a "$50
// setup" both ways) double-bills the client and double-counts the cost silently.
//
// WHY A SOFT WARNING, NOT A SCHEMA CONSTRAINT
// The two representations are BOTH legitimate on their own — a standalone rush
// or setup-fee service line is a real thing, and a rush or setup charge scoped
// to one decoration is a real thing. Only their co-occurrence is *suspicious*,
// and even then it can be correct (a setup on the goods AND a separate setup on
// an imprint run). A hard constraint would block real orders. So this is purely
// a nudge: it tells the rep "you have both — double-check", and NEVER gates a
// mutation or disables a button.
//
// Pure and dependency-free so the offline gate can EXECUTE it against fixtures
// rather than pattern-match source. Tolerant of missing/undefined decorations and
// charges arrays (leaner client payloads omit them).
//
// The spec/gate for this module is apps/api/src/db/qa-duplicate-fee-warning.ts
// (`pnpm --filter @merchd/api run db:qa-duplicate-fee-warning:offline`).

/** The service-line types that represent a standalone fee. */
const FEE_SERVICE_TYPES = new Set(["rush", "setup_fee", "other_service"]);
/** The decoration-charge types that represent the SAME economic fee. */
const FEE_CHARGE_TYPES = new Set(["rush", "setup", "other"]);

/** The minimum shape the check needs off a line: a service type and, optionally,
 *  decorations that optionally carry charges. Extra fields are ignored. */
export interface DuplicateFeeLine {
  serviceType: string;
  decorations?: Array<{
    charges?: Array<{ chargeType: string }> | null;
  }> | null;
}

export interface DuplicateFeeResult {
  /** True when any line is itself a fee service line (rush / setup_fee / other_service). */
  hasFeeLine: boolean;
  /** True when any decoration on any line carries a fee charge (rush / setup / other). */
  hasFeeCharge: boolean;
  /** The soft warning: both a fee line AND a fee charge are present. */
  warns: boolean;
}

/**
 * Detect the rush/setup/other double-entry pattern across an order's lines.
 *
 * `warns = hasFeeLine && hasFeeCharge` — the same fee is representable both as a
 * service line and as a decoration charge, and having one of each is the case
 * worth a second look. Never throws; a missing decorations or charges array is
 * treated as "none".
 */
export function detectDuplicateFeeEntry(
  lines: readonly DuplicateFeeLine[],
): DuplicateFeeResult {
  let hasFeeLine = false;
  let hasFeeCharge = false;

  for (const line of lines ?? []) {
    if (FEE_SERVICE_TYPES.has(line.serviceType)) hasFeeLine = true;
    for (const decoration of line.decorations ?? []) {
      for (const charge of decoration.charges ?? []) {
        if (FEE_CHARGE_TYPES.has(charge.chargeType)) hasFeeCharge = true;
      }
    }
  }

  return { hasFeeLine, hasFeeCharge, warns: hasFeeLine && hasFeeCharge };
}
